'use client';

/**
 * The bell (docs/NOTIFICATIONS.md): top right, just left of the Camps tent in
 * the header, and beside the profile in the Camps and Games bars.
 *
 *   click        a dropdown with the latest notifications and "Mark all as read"
 *   "Show all"   a panel that slides in from the right with every notification
 *
 * The red count is mine + (for admins) the Site list. Clicking a notification
 * marks it read and opens what it is about; `#credit` opens the wallet in Settings.
 */

import { NAV_BTN } from '../nav/styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth';
import SettingsDialog, { type SettingsTab } from '../SettingsDialog';
import { useNotifyCounts, fetchNotices, markRead, type Notice } from '@/lib/notifications';
import { NoticeRow, NoticeList, EmptyNotices, Skeleton, BellGlyph } from './NoticeList';

/** Where a notification goes: a page, or a Settings tab. */
export function useOpenNotice(close: () => void) {
  const router = useRouter();
  const [settings, setSettings] = useState<SettingsTab | null>(null);
  const open = useCallback((n: Notice) => {
    close();
    if (n.href.startsWith('#')) { setSettings(n.href.slice(1) as SettingsTab); return; }
    if (n.href.startsWith('/')) router.push(n.href);
  }, [close, router]);
  const dialog = settings ? <SettingsDialog initialTab={settings} onClose={() => setSettings(null)} /> : null;
  return { open, dialog };
}

export default function NotificationBell() {
  const { loaded, user } = useAuth();
  const c = useNotifyCounts(!!user);
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState(false);
  const [rows, setRows] = useState<Notice[] | null>(null);
  const [view, setView] = useState<'mine' | 'site'>('mine');
  const wrap = useRef<HTMLDivElement>(null);
  const { open: openNotice, dialog } = useOpenNotice(useCallback(() => { setOpen(false); setPanel(false); }, []));

  useEffect(() => {
    if (!open) return;
    setRows(null);
    fetchNotices(view === 'site' ? 'site' : 'all').then((r) => setRows(r.notifications.slice(0, 8))).catch(() => setRows([]));
  }, [open, view]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    const onBlur = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('blur', onBlur);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true); window.removeEventListener('blur', onBlur); };
  }, [open]);

  if (!loaded || !user) return null;
  const unread = c.mine + c.site;
  const pick = (n: Notice) => {
    if (!n.read) { setRows((x) => x?.map((y) => (y.id === n.id ? { ...y, read: true } : y)) ?? null); void markRead({ ids: [n.id] }); }
    openNotice(n);
  };

  const btn = NAV_BTN + (open ? ' bg-ink-100 text-ink-900 dark:bg-white/[0.08] dark:text-white' : '');

  return (
    <div ref={wrap} className="relative shrink-0">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="dialog" aria-expanded={open}
              aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'} title="Notifications"
              className={'relative ' + btn}>
        <BellGlyph className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-rose-500 px-1 text-center text-[10.5px] font-bold leading-[18px] text-white ring-2 ring-white dark:ring-ink-900">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div role="dialog" aria-label="Notifications"
             className="fixed inset-x-2 top-16 z-[130] flex max-h-[min(560px,calc(100dvh-5rem))] flex-col overflow-hidden rounded-2xl border border-ink-200 bg-white text-ink-900 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.35)]
                        dark:border-white/10 dark:bg-ink-900 dark:text-ink-50 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[380px]">
          <div className="flex items-center gap-2 border-b border-ink-200 px-4 py-3 dark:border-white/10">
            <h2 className="text-[15px] font-bold">Notifications</h2>
            {c.admin && (
              <div className="ml-1 flex rounded-full bg-ink-100 p-0.5 text-[12px] font-semibold dark:bg-white/[0.06]">
                {(['mine', 'site'] as const).map((v) => (
                  <button key={v} type="button" onClick={() => setView(v)} aria-pressed={view === v}
                          className={'rounded-full px-2.5 py-1 ' + (view === v ? 'bg-white shadow-sm dark:bg-ink-700' : 'text-ink-500 dark:text-ink-400')}>
                    {v === 'mine' ? 'Mine' : 'Site'}{(v === 'mine' ? c.mine : c.site) > 0 && <span className="ml-1 text-rose-500">{v === 'mine' ? c.mine : c.site}</span>}
                  </button>
                ))}
              </div>
            )}
            <button type="button" className="ml-auto rounded-full px-2 py-1 text-[12.5px] font-semibold text-accent-700 hover:bg-accent-50 dark:text-accent-300 dark:hover:bg-white/[0.06]"
                    onClick={async () => { setRows((x) => x?.map((y) => ({ ...y, read: true })) ?? null); await markRead({ tab: view === 'site' ? 'site' : 'all' }); }}>
              Mark all as read
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {!rows && <Skeleton rows={3} />}
            {rows && !rows.length && <EmptyNotices tab={view === 'site' ? 'site' : 'all'} />}
            {rows && rows.length > 0 && <ul className="divide-y divide-ink-100 dark:divide-white/[0.06]">{rows.map((n) => <NoticeRow key={n.id} n={n} onOpen={pick} compact />)}</ul>}
          </div>
          <button type="button" onClick={() => { setOpen(false); setPanel(true); }}
                  className="min-h-[46px] border-t border-ink-200 text-[13.5px] font-bold text-accent-700 hover:bg-ink-50 dark:border-white/10 dark:text-accent-300 dark:hover:bg-white/[0.04]">
            Show all
          </button>
        </div>
      )}

      {panel && <NotificationPanel admin={c.admin} mine={c.mine} site={c.site} initialTab={view === 'site' ? 'site' : 'all'} onClose={() => setPanel(false)} onOpen={openNotice} />}
      {dialog}
    </div>
  );
}

/** "Show all": every notification in a panel that slides in from the right, over the page. */
export function NotificationPanel({ admin, mine, site, initialTab, onClose, onOpen }: {
  admin: boolean; mine: number; site: number; initialTab: 'all' | 'site'; onClose: () => void; onOpen: (n: Notice) => void;
}) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const t = requestAnimationFrame(() => setShown(true));
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', key, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { cancelAnimationFrame(t); document.removeEventListener('keydown', key, true); document.body.style.overflow = prev; };
  }, [onClose]);
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="fixed inset-0 z-[140]" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div aria-hidden className={'absolute inset-0 bg-black/40 transition-opacity duration-200 ' + (shown ? 'opacity-100' : 'opacity-0')} onClick={onClose} />
      <aside role="dialog" aria-modal="true" aria-label="All notifications"
             className={'absolute inset-y-0 right-0 flex w-full max-w-[440px] flex-col bg-white text-ink-900 shadow-2xl transition-transform duration-200 ease-out dark:bg-ink-900 dark:text-ink-50 ' +
               (shown ? 'translate-x-0' : 'translate-x-full')}>
        <header className="flex items-center gap-3 border-b border-ink-200 px-4 py-3.5 dark:border-white/10">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-accent-50 text-accent-700 dark:bg-white/[0.06] dark:text-accent-300"><BellGlyph className="h-5 w-5" /></span>
          <h2 className="text-[16px] font-bold">Notifications</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="ml-auto grid h-10 w-10 place-items-center rounded-full text-ink-500 hover:bg-ink-100 dark:hover:bg-white/[0.06]">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </header>
        <NoticeList admin={admin} mineCount={mine} siteCount={site} initialTab={initialTab} onOpen={onOpen} />
      </aside>
    </div>,
    document.body,
  );
}
